"use client";

import { useEffect, useState } from "react";

import PortableText from "@/components/shared/portable-text";
import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselOptions,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { icon } from "@/components/ui/icons";
import { ModuleCaseStudiesCarousel } from "@/lib/types";
import { cn } from "@/lib/ui/cn";

import { Indicators } from "./Indicators.client";

type CaseStudiesCarouselProps = {
  items: ModuleCaseStudiesCarousel["caseStudies"];
};

const carouselOpts: CarouselOptions = {
  align: "start",
  slidesToScroll: 1,
  containScroll: "keepSnaps",
};

const KeyboardArrowRightIcon = icon("KeyboardArrowRight");

export function CaseStudiesCarousel({ items }: CaseStudiesCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <div className="-mx-padding-global gap-gap-element-lg flex flex-col">
      <Carousel setApi={setApi} opts={carouselOpts}>
        <CarouselContent className="px-padding-global" rootClassName="px-padding-global">
          {items.map((caseStudy, index) => {
            const isActive = current - 1 === index;

            return (
              <CarouselItem
                key={caseStudy._key}
                className={cn("not-first:pl-gap-element-lg! basis-auto")}
              >
                <div
                  className={cn(
                    "tablet:max-w-[700px] desktop:max-w-[px] tablet:min-h-[517px] tablet:flex-row-reverse relative flex h-full max-w-[320px] flex-col rounded-lg bg-white bg-blend-soft-light transition-all before:absolute before:inset-0 before:rounded-lg before:bg-[linear-gradient(180deg,rgba(189,212,252,0.2)_0%,rgba(178,178,179,0.2)_100%)] before:backdrop-blur-xl before:transition-opacity",
                    isActive
                      ? "before:opacity-0"
                      : "border-indigo-dark border bg-transparent before:opacity-100",
                  )}
                >
                  {caseStudy.media && (
                    <ResponsiveImage
                      src={caseStudy.media}
                      width={320}
                      height={180}
                      className="tablet:max-w-[291px] tablet:shrink-0 tablet:rounded-l-none relative z-10 aspect-4/3 h-auto w-full rounded-lg object-cover object-top"
                      useNextImage
                    />
                  )}
                  <div
                    className={cn(
                      `py-padding-element-lg px-padding-element-xl gap-gap-element-lg relative z-10 flex flex-col transition-all ${!caseStudy.cta?.label ? "justify-center" : ""}`,
                      isActive ? "text-neutral-darkest opacity-100" : "text-white opacity-50",
                    )}
                  >
                    <div className="gap-gap-element-lg pt-padding-element-lg flex flex-col">
                      {caseStudy.logo && (
                        <div className="tablet:h-[100px] relative h-[70px]">
                          <ResponsiveImage
                            src={caseStudy.logo}
                            fill
                            className="object-contain object-left"
                            imageOptions={{ desktopWidth: 100 }}
                          />
                        </div>
                      )}
                      <h4 className="typography-heading-4">{caseStudy.title}</h4>
                    </div>
                    {caseStudy.subtitle && (
                      <p className="typography-text-medium-medium">{caseStudy.subtitle}</p>
                    )}
                    <PortableText
                      value={caseStudy.body}
                      className={`typography-text-regular-normal line-clamp-5 ${isActive ? "**:text-neutral-darkest" : "**:text-white"}`}
                    />
                    {caseStudy.cta?.label && caseStudy.cta?.href ? (
                      <a href={caseStudy.cta.href.manualUrl}>
                        <Button
                          variant="link"
                          className={cn(
                            "transition-colors",
                            isActive ? "text-neutral-darkest" : "text-white",
                          )}
                        >
                          {caseStudy.cta.label}
                          <KeyboardArrowRightIcon />
                        </Button>
                      </a>
                    ) : null}
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselPrevious className="tablet:block left-0 ml-1 hidden" />
        <CarouselNext className="tablet:block right-0 mr-1 hidden" />
        <Indicators />
      </Carousel>
    </div>
  );
}
